import type { JSX } from 'react';

/** Shared inner container: every marketing band is full-bleed, its content is not. */
export function Band({
  children,
  background,
  className = '',
}: {
  readonly children: JSX.Element | readonly JSX.Element[];
  /** CSS colour for the full-bleed background. Omit for the page ground. */
  readonly background?: string;
  readonly className?: string;
}): JSX.Element {
  return (
    <section
      className={`w-full px-6 py-14 sm:py-20 ${className}`}
      {...(background !== undefined ? { style: { backgroundColor: background } } : {})}
    >
      <div className="mx-auto w-full max-w-[76rem]">{children}</div>
    </section>
  );
}

/** Section heading with an optional coral-accented tail, matching the marketing site's treatment. */
export function BandHeading({
  lead,
  accent,
  level = 2,
  className = '',
}: {
  readonly lead: string;
  readonly accent?: string;
  readonly level?: 1 | 2;
  readonly className?: string;
}): JSX.Element {
  const Tag = level === 1 ? 'h1' : 'h2';
  /*
   * FLUID, because the marketing site is. nourishedrx.com/dietitian-support sizes its type in
   * viewport units — measured across two widths, h1 is 3.2vw and h2 2.5vw — so it renders h1 at
   * 46px on a 1440 screen and 62px on a 1920 one. A fixed size can only match one width: this page
   * used to be fixed and small (41px h1), which is why it read undersized next to the real site on
   * a wide monitor.
   *
   * clamp() reproduces that ramp between floor and ceiling. The floor keeps headings readable on a
   * phone, where a pure vw size would collapse; the ceiling stops them running away on an ultrawide,
   * which the marketing site does not guard against.
   */
  const size =
    level === 1 ? 'text-[clamp(2rem,3.2vw,4rem)]' : 'text-[clamp(1.6rem,2.5vw,3.125rem)]';
  return (
    <Tag
      className={`${size} font-bold leading-tight text-balance text-[var(--forest)] ${className}`}
    >
      {lead}
      {accent !== undefined && <span className="text-[var(--coral)]">{accent}</span>}
    </Tag>
  );
}
